import { request, BASE_URL } from '@/api/client';
import type { SSEEvent } from '@/services/chatService';
import {
  readSSEStream,
  handleStreamFetchError,
  handleStreamResponseError,
} from '@/services/chatService';

/** Group-chat metadata pairing two echoes; mirrors the backend `GroupChatResponse`. */
export interface GroupChatData {
  id: string;
  name: string;
  echo_id_1: string;
  echo_id_2: string;
  echo_1_name: string;
  echo_2_name: string;
  echo_1_image: string | null;
  echo_2_image: string | null;
  created_at: string;
  last_message?: string | null;
  last_message_at?: string | null;
}

/** Paginated history page returned by `groupChatService.getHistory`. */
export interface GroupHistoryResponse {
  messages: GroupMessageData[];
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
}

/** Single message in a group chat; either user-authored or AI-authored from one of the two echoes. */
export interface GroupMessageData {
  id: string;
  group_chat_id: string;
  echo_id: string | null;
  sender_type: 'user' | 'ai';
  content: string;
  audio_url: string;
  audio_url_azure: string;
  audio_url_elevenlabs: string;
  suggested_questions: Array<{ id: string; text: string }>;
  is_read: boolean;
  created_at: string;
  rating?: {
    is_liked: boolean;
    is_disliked: boolean;
    is_super_liked: boolean;
  };
}

interface TranscribeAudioResponse {
  transcribed_text: string;
  error?: string;
}

/** Group-chat endpoints — list, create, delete groups, fetch paginated history, transcribe voice input, stream replies. */
export const groupChatService = {
  /**
   * Upload audio for a group chat and transcribe via Azure STT.
   * Returns transcribed text so the caller can feed it into streamChat.
   * No `language` field is sent so the backend's auto-detect default
   * (en-US/ro-RO/es-ES/fr-FR) kicks in — see DEC-250 / DEC-266.
   */
  transcribeAudio: async (groupId: string, audioBlob: Blob): Promise<TranscribeAudioResponse> => {
    const formData = new FormData();

    // Sanitize MIME type (strip ';codecs=...') — same pattern as chatService.sendAudioMessage
    const rawType =
      typeof audioBlob.type === 'string' && audioBlob.type.length > 0
        ? audioBlob.type
        : 'audio/webm';
    const cleanType = rawType.split(';')[0];

    let extension = 'webm';
    if (cleanType === 'audio/mp4') extension = 'm4a';
    else if (cleanType === 'audio/mpeg') extension = 'mp3';
    else if (cleanType === 'audio/wav') extension = 'wav';
    else if (cleanType === 'audio/ogg') extension = 'ogg';

    const safeType: string = cleanType || 'audio/webm';
    const file = new File([audioBlob], `voice_message.${extension}`, { type: safeType });
    formData.append('audio', file);

    return request<TranscribeAudioResponse>(`/group-chat/${encodeURIComponent(groupId)}/audio`, {
      method: 'POST',
      body: formData,
      auth: true,
    });
  },

  createGroup: (echoId1: string, echoId2: string): Promise<GroupChatData> =>
    request<GroupChatData>('/group-chat', {
      method: 'POST',
      body: JSON.stringify({ echo_id_1: echoId1, echo_id_2: echoId2 }),
      auth: true,
    }),

  getGroups: (): Promise<GroupChatData[]> =>
    request<GroupChatData[]>('/group-chat', {
      method: 'GET',
      auth: true,
    }),

  deleteGroup: (groupId: string): Promise<void> =>
    request<void>(`/group-chat/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
      auth: true,
    }),

  getHistory: (
    groupId: string,
    offset = 0,
    limit = 20,
    signal?: AbortSignal
  ): Promise<GroupHistoryResponse> =>
    request<GroupHistoryResponse>(
      `/group-chat/${encodeURIComponent(groupId)}/history?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`,
      {
        method: 'GET',
        auth: true,
        signal: signal ?? null,
      }
    ),

  clearHistory: (groupId: string): Promise<void> =>
    request<void>(`/group-chat/${encodeURIComponent(groupId)}/history`, {
      method: 'DELETE',
      auth: true,
    }),

  streamChat: async (
    groupId: string,
    content: string,
    ttsProvider: 'azure' | 'elevenlabs',
    onEvent: (evt: SSEEvent) => void,
    signal?: AbortSignal,
    generateAudio = true
  ): Promise<void> => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Not authenticated');

    const url = `${BASE_URL}/group-chat/${encodeURIComponent(groupId)}/stream?content=${encodeURIComponent(content)}&tts_provider=${encodeURIComponent(ttsProvider)}&generate_audio=${generateAudio}`;

    let resp: Response;
    try {
      resp = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        signal: signal ?? null,
      });
    } catch (networkError) {
      handleStreamFetchError(networkError);
    }

    if (!resp.ok || !resp.body) {
      return handleStreamResponseError(resp);
    }

    const reader = resp.body.getReader();
    await readSSEStream(reader, onEvent);
  },
};
